/**
 * Login.tsx — Supabase Auth integration (Task #34).
 * Uses supabase.signInWithPassword; no server session cookie needed.
 *
 * Task #69: Cloudflare Turnstile invisible security gate (Phase 1).
 * Task #70: Boarding Pass Clearance™ (Phase 2) — the branded puzzle overlay
 *   wraps useTurnstile().execute() and verifyTurnstileToken() internally.
 *   Login never touches Turnstile directly; it renders <BoardingPassClearance>
 *   which handles the full verification flow and calls onVerified() on success.
 */

import { useState } from 'react';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { User, Lock, Eye, EyeOff, Globe, LogIn } from 'lucide-react';
import WorkRoamerBrand from '@/components/WorkRoamerBrand';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { getGetMeQueryKey } from '@workspace/api-client-react';
import BoardingPassClearance from '@/components/auth/BoardingPassClearance';

function GlowDivider() {
  return (
    <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
      <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.5) 100%)' }} />
      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6', margin: '0 3px', flexShrink: 0, boxShadow: '0 0 8px 2px rgba(59,130,246,0.7), 0 0 18px rgba(59,130,246,0.35)' }} />
      <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(59,130,246,0.5) 0%, transparent 100%)' }} />
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: '#060f1f',
  border: '1.5px solid rgba(59,130,246,0.22)',
  borderRadius: '8px',
  color: '#e2e8f0',
  fontSize: '14px',
  padding: '13px 14px 13px 44px',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

export default function Login() {
  const [email,            setEmail]            = useState('');
  const [password,         setPassword]         = useState('');
  const [showPass,         setShowPass]         = useState(false);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState<string | null>(null);
  const [showBoardingPass, setShowBoardingPass] = useState(false);

  const [, setLocation] = useLocation();
  const queryClient     = useQueryClient();
  const { signIn }      = useSupabaseAuth();

  // ── Step 1: validate form → show Boarding Pass puzzle ────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError('Please enter your email and password.');
      return;
    }

    // Show the Boarding Pass Clearance™ overlay.
    // It runs useTurnstile().execute() + verifyTurnstileToken() internally
    // and calls onVerified() when both succeed.
    setShowBoardingPass(true);
  };

  // ── Step 2: called by BoardingPassClearance after Turnstile verified ──────
  const handleBoardingPassVerified = async () => {
    setShowBoardingPass(false);
    setLoading(true);
    try {
      const { error: authError } = await signIn(email.trim(), password);
      if (authError) {
        setError(humaniseError(authError.message));
        return;
      }
      // Invalidate /auth/me so Shell picks up the provisioned user immediately.
      await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      try { sessionStorage.setItem('nomad:fresh-signup', '0'); } catch { /* ignore */ }
      setLocation('/home');
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleBoardingPassError = (msg: string) => {
    setShowBoardingPass(false);
    setError(msg);
  };

  return (
    <>
      {/* ── Boarding Pass Clearance™ overlay (Phase 2) ─────────────────── */}
      {showBoardingPass && (
        <BoardingPassClearance
          onVerified={handleBoardingPassVerified}
          onError={handleBoardingPassError}
          onClose={() => setShowBoardingPass(false)}
        />
      )}

      {/* ── Login form ─────────────────────────────────────────────────── */}
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(ellipse 80% 70% at 50% 35%, #0b2060 0%, #04112b 55%, #010a1a 100%)',
        padding: '20px 16px', fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
      }}>
        <div style={{
          width: '100%', maxWidth: '430px', boxSizing: 'border-box' as const,
          padding: 'clamp(24px,6vw,40px) clamp(16px,5.5vw,32px) clamp(20px,4vw,28px)',
          overflow: 'hidden' as const,
          background: 'linear-gradient(175deg, #0d2048 0%, #071428 60%, #040d1e 100%)',
          borderRadius: '20px', border: '1.5px solid rgba(59,130,246,0.45)',
          boxShadow: ['0 0 0 1px rgba(59,130,246,0.08)', '0 0 40px rgba(37,99,235,0.30)', '0 0 80px rgba(37,99,235,0.12)', 'inset 0 1px 0 rgba(255,255,255,0.04)'].join(', '),
          display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
        }}>
          <WorkRoamerBrand width={300} style={{ marginBottom: 4 }} />
          <p style={{ margin: '6px 0 6px', color: '#8fa8c8', fontSize: '13px', textAlign: 'center', fontWeight: 600 }}>
            Your next opportunity is already waiting.
          </p>
          <p style={{ margin: '0 0 20px', color: 'rgba(143,168,200,0.6)', fontSize: '11px', textAlign: 'center', letterSpacing: '0.02em', lineHeight: 1.5 }}>
            Daily missions · Real earnings · Built for digital nomads
          </p>
          <GlowDivider />

          <form onSubmit={handleLogin} style={{ width: '100%', marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Email */}
            <div>
              <label style={{ display: 'block', color: '#3b82f6', fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.16em', textTransform: 'uppercase' as const, marginBottom: '8px' }}>
                Email Address
              </label>
              <div style={{ position: 'relative' }}>
                <User size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#4b6fa8', pointerEvents: 'none' }} />
                <input
                  type="email" autoComplete="email" required value={email}
                  onChange={e => setEmail(e.target.value)} placeholder="Enter your email address"
                  disabled={loading} style={{ ...inputStyle, opacity: loading ? 0.7 : 1 }}
                  onFocus={e => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.65)')}
                  onBlur={e  => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.22)')}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ color: '#3b82f6', fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.16em', textTransform: 'uppercase' as const }}>Password</label>
                <a href="/forgot-password" style={{ color: '#4b6fa8', fontSize: '11px', textDecoration: 'none' }}>Forgot password?</a>
              </div>
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#4b6fa8', pointerEvents: 'none' }} />
                <input
                  type={showPass ? 'text' : 'password'} autoComplete="current-password" required value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="Enter your password"
                  disabled={loading} style={{ ...inputStyle, padding: '13px 44px 13px 44px', opacity: loading ? 0.7 : 1 }}
                  onFocus={e => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.65)')}
                  onBlur={e  => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.22)')}
                />
                <button type="button" onClick={() => setShowPass(v => !v)} aria-label={showPass ? 'Hide' : 'Show'}
                  style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#4b6fa8', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && <p style={{ color: '#f87171', fontSize: '12px', textAlign: 'center', margin: 0 }}>{error}</p>}

            <button type="submit" disabled={loading} style={{
              width: '100%',
              background: loading ? 'rgba(37,99,235,0.45)' : 'linear-gradient(90deg, #1a3fcc 0%, #2563eb 45%, #3b82f6 100%)',
              border: 'none', borderRadius: '8px', color: '#ffffff',
              fontSize: '13px', fontWeight: '800', letterSpacing: '0.16em', textTransform: 'uppercase' as const,
              padding: '16px 24px', cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginTop: '4px',
              boxShadow: loading ? 'none' : '0 4px 18px rgba(37,99,235,0.45), inset 0 1px 0 rgba(255,255,255,0.12)',
              fontFamily: 'inherit', opacity: loading ? 0.65 : 1,
            }}>
              <LogIn size={18} strokeWidth={2.5} />
              {loading ? 'Signing in…' : 'Access Roamer Terminal'}
            </button>
          </form>

          <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', margin: '24px 0 14px' }}>
            <div style={{ flex: 1, height: '1px', background: 'rgba(59,130,246,0.2)' }} />
            <Globe size={15} style={{ color: '#3b6cb0', flexShrink: 0 }} />
            <div style={{ flex: 1, height: '1px', background: 'rgba(59,130,246,0.2)' }} />
          </div>

          <p style={{ color: '#8fa8c8', fontSize: '13px', margin: '0 0 14px', textAlign: 'center' }}>
            Built for people <span style={{ color: '#3b82f6' }}>who don't stay put.</span>
          </p>
          <p style={{ color: '#3a5070', fontSize: '11.5px', margin: '0 0 18px', textAlign: 'center' }}>
            New to WorkRoamer?{' '}
            <a href="/signup" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}>Create account</a>
          </p>
          <div style={{ borderTop: '1px solid rgba(59,130,246,0.12)', width: '100%', paddingTop: '16px' }}>
            <p style={{ color: '#2a3d58', fontSize: '11px', margin: 0, textAlign: 'center' }}>
              © 2026 <strong style={{ color: '#3a5070' }}>WorkRoamer™</strong>{' '}•{' '}Built for people who work from anywhere
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function humaniseError(msg: string): string {
  const s = msg.toLowerCase();
  if (s.includes('invalid login') || s.includes('invalid credentials') || s.includes('wrong password'))
    return 'Invalid email or password.';
  if (s.includes('email not confirmed'))
    return 'Please verify your email before signing in. Check your inbox.';
  if (s.includes('too many'))
    return 'Too many attempts. Please wait a few minutes and try again.';
  if (s.includes('user not found') || s.includes('no user found'))
    return 'No account found with that email address.';
  return msg || 'Sign in failed. Please try again.';
}
